/**
 * API Client for MCP Hub Universal
 * Handles all communication with the backend with JWT authentication
 * @version 2.0.0
 * @updated 2025-10-29 - Fixed API URL for Cloudflare Workers deployment
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://mcphub.automators.work';
const API_VERSION = '2.0.0'; // Force bundle hash change

class APIClient {
  private baseURL: string;
  private version: string = API_VERSION;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
    // Log version for debugging (and to satisfy TypeScript unused variable check)
    if (import.meta.env.DEV) {
      console.log(`MCP Hub API Client v${this.version} - ${this.baseURL}`);
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;

    // Get access token from localStorage
    const accessToken = localStorage.getItem('access_token');

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add Authorization header if token exists
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    // Merge with provided headers
    if (options.headers) {
      Object.assign(headers, options.headers);
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Handle 401 Unauthorized - token expired
    if (response.status === 401) {
      // Try to refresh token
      const refreshed = await this.tryRefreshToken();
      if (refreshed) {
        // Retry request with new token
        return this.request(endpoint, options);
      } else {
        // Refresh failed, clear tokens and throw
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        throw new Error('Session expired. Please login again.');
      }
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);

      // Extract error message from various possible formats
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

      if (errorData) {
        if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      }

      const error = new Error(errorMessage) as any;
      error.status = response.status;
      error.response = { data: errorData };
      throw error;
    }

    return response.json();
  }

  private async tryRefreshToken(): Promise<boolean> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      return false;
    }

    try {
      const response = await fetch(`${this.baseURL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      // Backend returns tokens in a nested structure
      if (data.tokens) {
        localStorage.setItem('access_token', data.tokens.access_token);
        if (data.tokens.refresh_token) {
          localStorage.setItem('refresh_token', data.tokens.refresh_token);
        }
      } else {
        // Fallback for old format
        localStorage.setItem('access_token', data.accessToken || data.access_token);
        if (data.refreshToken || data.refresh_token) {
          localStorage.setItem('refresh_token', data.refreshToken || data.refresh_token);
        }
      }
      return true;
    } catch {
      return false;
    }
  }

  // ========== AUTH API ==========

  auth = {
    register: async (email: string, password: string) => {
      const response = await this.request<{
        success: boolean;
        tokens: { access_token: string; refresh_token?: string };
        user: any;
      }>('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      // Transform response to match expected format
      return {
        accessToken: response.tokens.access_token,
        refreshToken: response.tokens.refresh_token
      };
    },

    login: async (email: string, password: string) => {
      const response = await this.request<{
        success: boolean;
        tokens: { access_token: string; refresh_token?: string };
        user: any;
      }>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      // Transform response to match expected format
      return {
        accessToken: response.tokens.access_token,
        refreshToken: response.tokens.refresh_token
      };
    },

    logout: async () => {
      return this.request<{ success: boolean }>('/api/auth/logout', {
        method: 'POST',
      });
    },

    me: async () => {
      return this.request<any>('/api/auth/me');
    },

    verifyEmail: async (token: string) => {
      return this.request<{ success: boolean }>(`/api/auth/verify-email?token=${token}`);
    },

    resendVerification: async () => {
      return this.request<{ success: boolean }>('/api/auth/resend-verification', {
        method: 'POST',
      });
    },

    requestPasswordReset: async (email: string) => {
      return this.request<{ success: boolean }>('/api/auth/request-reset', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
    },

    resetPassword: async (token: string, newPassword: string) => {
      return this.request<{ success: boolean }>('/api/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, newPassword }),
      });
    },
  };

  // ========== USERS API ==========

  users = {
    me: async () => {
      return this.request<any>('/api/users/me');
    },

    trialStatus: async () => {
      return this.request<any>('/api/users/trial-status');
    },

    upgrade: async (plan: 'pro' | 'enterprise') => {
      return this.request<any>('/api/users/upgrade', {
        method: 'POST',
        body: JSON.stringify({ plan }),
      });
    },
  };

  // ========== CLIENTS API ==========

  clients = {
    list: async () => {
      const response = await this.request<{ clients: any[] }>('/api/clients');
      return response.clients;
    },

    get: async (id: string) => {
      const response = await this.request<{ client: any }>(`/api/clients/${id}`);
      return response.client;
    },

    create: async (name: string, description?: string) => {
      const response = await this.request<{ client: any }>('/api/clients', {
        method: 'POST',
        body: JSON.stringify({ name, description }),
      });
      return response.client;
    },

    update: async (id: string, data: {
      name?: string;
      description?: string;
      color?: string;
      icon?: string;
      notes?: string;
      default_permission?: 'allow_all' | 'deny_all';
    }) => {
      const response = await this.request<{ client: any }>(`/api/clients/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return response.client;
    },

    delete: async (id: string) => {
      return this.request<{ success: boolean }>(`/api/clients/${id}`, {
        method: 'DELETE',
      });
    },

    stats: async (id: string) => {
      return this.request<any>(`/api/clients/${id}/stats`);
    },

    limits: async (id: string) => {
      return this.request<any>(`/api/clients/${id}/limits`);
    },

    // Permissions management
    permissions: {
      list: async (clientId: string) => {
        const response = await this.request<{
          client_id: string;
          default_permission: 'allow_all' | 'deny_all';
          items: any[];
        }>(`/api/clients/${clientId}/permissions`);
        return response;
      },

      update: async (clientId: string, itemId: string, permissionType: 'allow' | 'deny' | null) => {
        const response = await this.request<{ success: boolean; permission?: any }>(`/api/clients/${clientId}/permissions/${itemId}`, {
          method: 'PUT',
          body: JSON.stringify({ permission_type: permissionType }),
        });
        return response;
      },

      bulkUpdate: async (clientId: string, permissions: Array<{ item_id: string; permission_type: 'allow' | 'deny' | null }>) => {
        const response = await this.request<{ success: boolean; results: any[] }>(`/api/clients/${clientId}/permissions/bulk`, {
          method: 'POST',
          body: JSON.stringify({ permissions }),
        });
        return response;
      },

      deleteAll: async (clientId: string) => {
        const response = await this.request<{ success: boolean; deleted_count: number }>(`/api/clients/${clientId}/permissions/all`, {
          method: 'DELETE',
        });
        return response;
      },
    },
  };

  // ========== SERVERS API ==========

  servers = {
    list: async (clientId?: string) => {
      const query = clientId ? `?client_id=${clientId}` : '';
      const response = await this.request<{ servers: any[] }>(`/api/servers${query}`);
      // Transform snake_case from backend to camelCase for frontend
      return (response.servers || []).map((server: any) => ({
        id: server.id,
        serverName: server.server_name,
        serverType: server.server_type,
        status: server.status,
        lastSync: server.last_sync,
        lastError: server.last_error,
        capabilities: server.capabilities,
        clientId: server.client_id
      }));
    },

    get: async (id: string) => {
      const response = await this.request<{ server: any }>(`/api/servers/${id}`);
      return response.server;
    },

    connect: async (serverName: string, url: string, clientId?: string) => {
      const response = await this.request<{ server: any }>('/api/servers/connect', {
        method: 'POST',
        body: JSON.stringify({
          client_id: clientId,
          name: serverName,
          url,
          type: 'auto' // Let backend auto-detect HTTP vs SSE
        }),
      });
      return response.server;
    },

    update: async (id: string, data: { server_name?: string; client_id?: string }) => {
      const response = await this.request<{ server: any }>(`/api/servers/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return response.server;
    },

    delete: async (id: string) => {
      return this.request<{ success: boolean }>(`/api/servers/${id}`, {
        method: 'DELETE',
      });
    },

    getDependencies: async (id: string) => {
      const response = await this.request<{
        success: boolean;
        server_id: string;
        server_name: string;
        dependencies: {
          clients: Array<{
            id: string;
            name: string;
            tools_count: number;
            prompts_count: number;
            resources_count: number;
          }>;
          tools: Array<{
            id: string;
            name: string;
            description?: string;
            dependent_prompts_count: number;
            is_critical: boolean;
            dependent_prompts: any[];
          }>;
          resources_count: number;
          prompts_count: number;
          total_affected_items: number;
          total_dependent_prompts: number;
          has_critical_tools: boolean;
          recommendation: string;
        };
      }>(`/api/servers/${id}/dependencies`);
      return response;
    },
  };

  // ========== QUERY API ==========

  query = {
    search: async (query: string, types?: string[], maxResults?: number) => {
      return this.request<{ results: any[]; total: number }>('/api/query', {
        method: 'POST',
        body: JSON.stringify({ query, ardfTypes: types, maxResults }),
      });
    },

    stats: async () => {
      return this.request<any>('/api/query/stats');
    },
  };

  // ========== EXECUTE API ==========

  execute = {
    tool: async (serverId: string, toolName: string, args: Record<string, any>) => {
      return this.request<{ result: any }>('/api/execute', {
        method: 'POST',
        body: JSON.stringify({ serverId, toolName, arguments: args }),
      });
    },

    history: async () => {
      return this.request<{ executions: any[] }>('/api/execute/history');
    },
  };

  // ========== API KEYS ==========

  apiKeys = {
    list: async () => {
      const response = await this.request<{ keys: any[] }>('/api/keys');
      return response.keys;
    },

    create: async (name: string, scopes: string[] = ['query', 'execute'], expiresInDays?: number) => {
      const response = await this.request<{ api_key: any }>('/api/keys', {
        method: 'POST',
        body: JSON.stringify({
          name,
          scopes,
          expires_in_days: expiresInDays,
        }),
      });
      return response.api_key;
    },

    delete: async (id: string) => {
      return this.request<{ success: boolean }>(`/api/keys/${id}`, {
        method: 'DELETE',
      });
    },

    revoke: async (id: string) => {
      return this.request<{ success: boolean }>(`/api/keys/${id}/revoke`, {
        method: 'POST',
      });
    },
  };

  // ========== CUSTOM PROMPTS API ==========

  prompts = {
    list: async () => {
      const response = await this.request<{ prompts: any[]; total: number }>('/api/prompts');
      return response;
    },

    get: async (id: string) => {
      const response = await this.request<{ prompt: any }>(`/api/prompts/${id}`);
      return response.prompt;
    },

    create: async (data: {
      name: string;
      description?: string;
      template: string;
      arguments?: Array<{ name: string; type: string; required?: boolean; description?: string }>;
      workflow?: any[];
      auto_execute?: boolean;
      tags?: string[];
      client_id?: string;
      flow_data?: any;
    }) => {
      const response = await this.request<{ prompt: any }>('/api/prompts', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return response.prompt;
    },

    update: async (id: string, data: {
      name?: string;
      description?: string;
      template?: string;
      arguments?: Array<{ name: string; type: string; required?: boolean; description?: string }>;
      workflow?: any[];
      auto_execute?: boolean;
      tags?: string[];
      flow_data?: any;
    }) => {
      const response = await this.request<{ prompt: any }>(`/api/prompts/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return response.prompt;
    },

    delete: async (id: string) => {
      return this.request<{ success: boolean }>(`/api/prompts/${id}`, {
        method: 'DELETE',
      });
    },

    execute: async (promptIdOrName: string, args: Record<string, any> = {}) => {
      // Support both prompt_id and prompt_name for flexibility
      const body: any = { ...args };
      if (promptIdOrName.startsWith('prompt_')) {
        body.prompt_id = promptIdOrName;
      } else {
        body.prompt_name = promptIdOrName;
      }

      return this.request<{
        prompt_id: string;
        prompt_name: string;
        filled_template: string;
        auto_execute: boolean;
        workflow_steps?: any[];
        instructions?: string;
      }>('/api/prompts/execute', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    // Prompt-Tool Relationships
    tools: {
      list: async (promptId: string) => {
        const response = await this.request<{ prompt_id: string; tools: any[]; total: number }>(`/api/prompts/${promptId}/tools`);
        return response;
      },

      add: async (promptId: string, data: { tool_id: string; is_required?: boolean; execution_order?: number }) => {
        const response = await this.request<any>(`/api/prompts/${promptId}/tools`, {
          method: 'POST',
          body: JSON.stringify(data),
        });
        return response;
      },

      update: async (promptId: string, relationId: string, data: { is_required?: boolean; execution_order?: number }) => {
        const response = await this.request<{ success: boolean }>(`/api/prompts/${promptId}/tools/${relationId}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        });
        return response;
      },

      remove: async (promptId: string, relationId: string) => {
        return this.request<{ success: boolean }>(`/api/prompts/${promptId}/tools/${relationId}`, {
          method: 'DELETE',
        });
      },
    },
  };

  // ========== NOTIFICATIONS ==========

  notifications = {
    list: async (filters?: { unread_only?: boolean; category?: string }) => {
      const params = new URLSearchParams()
      if (filters?.unread_only) params.append('unread_only', 'true')
      if (filters?.category) params.append('category', filters.category)

      const query = params.toString() ? `?${params.toString()}` : ''
      const response = await this.request<{ notifications: any[]; total: number; unread_count: number }>(`/api/notifications${query}`)
      return response
    },

    markAsRead: async (id: string) => {
      return this.request<{ success: boolean }>(`/api/notifications/${id}/read`, {
        method: 'PUT',
      })
    },

    markAllAsRead: async () => {
      return this.request<{ success: boolean }>('/api/notifications/read-all', {
        method: 'PUT',
      })
    },

    dismiss: async (id: string) => {
      return this.request<{ success: boolean }>(`/api/notifications/${id}/dismiss`, {
        method: 'PUT',
      })
    },
  }

  // ========== TOOLS ==========

  tools = {
    list: async () => {
      const response = await this.request<{ success: boolean; tools: any[]; total: number }>('/api/tools');
      return response;
    },

    getDependentPrompts: async (toolId: string) => {
      const response = await this.request<{
        success: boolean
        tool_id: string
        tool_name: string
        dependent_prompts: any[]
        impact_analysis: {
          total_prompts: number
          total_usage: number
          has_required_dependencies: boolean
          recommendation: string
        }
      }>(`/api/tools/${toolId}/dependent-prompts`)
      return response
    },

    getHealth: async (toolId: string) => {
      const response = await this.request<{
        success: boolean
        tool_id: string
        tool_name: string
        current_status: string
        metrics_24h: any
        lifetime_stats: any
        recent_checks: any[]
      }>(`/api/tools/${toolId}/health`)
      return response
    },

    delete: async (toolId: string) => {
      const response = await this.request<{ success: boolean; message: string }>(`/api/tools/${toolId}`, {
        method: 'DELETE',
      })
      return response
    },
  }

  // ========== ANALYTICS ==========

  analytics = {
    getOverview: async () => {
      const response = await this.request<{
        success: boolean;
        overview: {
          total_prompts: number;
          total_tools: number;
          active_clients: number;
          active_servers: number;
          total_executions: number;
        };
        trends: {
          prompts_7d: number;
          tools_7d: number;
          clients_7d: number;
        };
      }>('/api/analytics/overview');
      return response;
    },

    getUsage: async (params?: { start_date?: string; end_date?: string; type?: string }) => {
      const query = new URLSearchParams();
      if (params?.start_date) query.append('start_date', params.start_date);
      if (params?.end_date) query.append('end_date', params.end_date);
      if (params?.type) query.append('type', params.type);

      const queryString = query.toString() ? `?${query.toString()}` : '';
      const response = await this.request<{
        success: boolean;
        type: string;
        start_date: string;
        end_date: string;
        data_points: Array<{ date: string; count: number }>;
      }>(`/api/analytics/usage${queryString}`);
      return response;
    },

    getTopPrompts: async (limit = 10) => {
      const response = await this.request<{
        success: boolean;
        prompts: Array<{
          id: string;
          name: string;
          description: string;
          execution_count: number;
          last_used: string;
          created_at: string;
          success_rate: number;
        }>;
        total: number;
      }>(`/api/analytics/top-prompts?limit=${limit}`);
      return response;
    },

    getTopTools: async (limit = 10) => {
      const response = await this.request<{
        success: boolean;
        tools: Array<{
          id: string;
          name: string;
          description: string;
          prompts_using: number;
          server_id: string;
          total_calls: number;
          avg_latency_ms: number;
        }>;
        total: number;
      }>(`/api/analytics/top-tools?limit=${limit}`);
      return response;
    },

    getHealthStatus: async () => {
      const response = await this.request<{
        success: boolean;
        health_status: {
          servers: { connected: number; disconnected: number; error: number };
          tools: { available: number; degraded: number; down: number };
          prompts: { healthy: number; warning: number; error: number };
        };
      }>('/api/analytics/health-status');
      return response;
    },
  };

  // ========== RESOURCES (RAG) ==========

  resources = {
    list: async (clientId?: string) => {
      const query = clientId ? `?client_id=${clientId}` : '';
      const response = await this.request<{
        success: boolean;
        resources: Array<{
          id: string;
          name: string;
          description: string;
          resource_type: string;
          size_bytes: number;
          total_tokens: number;
          chunk_count: number;
          query_count: number;
          last_queried_at: string | null;
          created_at: string;
          tags: string[];
        }>;
        total: number;
      }>(`/api/resources${query}`);
      return response;
    },

    get: async (id: string) => {
      const response = await this.request<{
        success: boolean;
        resource: {
          id: string;
          name: string;
          description: string;
          resource_type: string;
          mime_type: string;
          size_bytes: number;
          total_tokens: number;
          chunk_count: number;
          query_count: number;
          last_queried_at: string | null;
          created_at: string;
          updated_at: string;
          tags: string[];
          recent_queries: Array<{
            query: string;
            answer: string;
            strategy: string;
            tokens_used: number;
            chunks_retrieved: number;
            confidence: string;
            created_at: string;
          }>;
        };
      }>(`/api/resources/${id}`);
      return response;
    },

    upload: async (data: {
      name: string;
      description?: string;
      content: string;
      resource_type?: string;
      mime_type?: string;
      client_id?: string;
      tags?: string[];
    }) => {
      const response = await this.request<{
        success: boolean;
        resource_id: string;
        message: string;
      }>('/api/resources', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return response;
    },

    query: async (id: string, query: string) => {
      const response = await this.request<{
        success: boolean;
        answer: string;
        citations: Array<{
          chunk_index?: number;
          content: string;
          relevance?: number;
        }>;
        confidence: 'high' | 'medium' | 'low';
        strategy: 'full-context' | 'hybrid-retrieval';
        tokens_used: number;
        chunks_retrieved: number;
      }>(`/api/resources/${id}/query`, {
        method: 'POST',
        body: JSON.stringify({ query }),
      });
      return response;
    },

    delete: async (id: string) => {
      const response = await this.request<{
        success: boolean;
        message: string;
      }>(`/api/resources/${id}`, {
        method: 'DELETE',
      });
      return response;
    },

    getChunks: async (id: string) => {
      const response = await this.request<{
        success: boolean;
        resource_id: string;
        chunks: Array<{
          id: string;
          chunk_index: number;
          content: string;
          token_count: number;
          start_position: number;
          end_position: number;
          section_title: string | null;
          section_level: number;
        }>;
        total: number;
      }>(`/api/resources/${id}/chunks`);
      return response;
    },

    getQueries: async (id: string, limit = 20) => {
      const response = await this.request<{
        success: boolean;
        resource_id: string;
        queries: Array<{
          id: string;
          query: string;
          answer: string;
          strategy: string;
          tokens_used: number;
          chunks_retrieved: number;
          confidence: string;
          created_at: string;
        }>;
        total: number;
      }>(`/api/resources/${id}/queries?limit=${limit}`);
      return response;
    },
  };

  // ========== URL RESOURCES ==========

  urlResources = {
    list: async (params?: { client_id?: string; url_type?: string; is_active?: boolean; limit?: number }) => {
      const query = new URLSearchParams();
      if (params?.client_id) query.append('client_id', params.client_id);
      if (params?.url_type) query.append('url_type', params.url_type);
      if (params?.is_active !== undefined) query.append('is_active', String(params.is_active));
      if (params?.limit) query.append('limit', String(params.limit));

      const queryString = query.toString();
      const url = `/api/url-resources${queryString ? `?${queryString}` : ''}`;

      const response = await this.request<{
        success: boolean;
        resources: Array<{
          id: string;
          url: string;
          name: string;
          description: string | null;
          usage_context: string;
          usage_keywords: string[];
          response_template: string;
          url_type: string;
          mime_type: string | null;
          is_active: number;
          use_count: number;
          last_used_at: string | null;
          created_at: string;
          updated_at: string;
          tags: string[];
        }>;
        total: number;
      }>(url);
      return response;
    },

    get: async (id: string) => {
      const response = await this.request<{
        success: boolean;
        resource: {
          id: string;
          url: string;
          name: string;
          description: string | null;
          usage_context: string;
          usage_keywords: string[];
          response_template: string;
          url_type: string;
          mime_type: string | null;
          is_active: number;
          use_count: number;
          last_used_at: string | null;
          created_at: string;
          updated_at: string;
          tags: string[];
        };
      }>(`/api/url-resources/${id}`);
      return response;
    },

    create: async (data: {
      url: string;
      name: string;
      description?: string;
      usage_context: string;
      usage_keywords?: string[];
      response_template: string;
      url_type?: string;
      client_id?: string;
      tags?: string[];
    }) => {
      const response = await this.request<{
        success: boolean;
        resource_id: string;
        message: string;
      }>('/api/url-resources', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return response;
    },

    update: async (id: string, data: {
      name?: string;
      description?: string;
      usage_context?: string;
      usage_keywords?: string[];
      response_template?: string;
      url_type?: string;
      is_active?: boolean;
      tags?: string[];
    }) => {
      const response = await this.request<{
        success: boolean;
        message: string;
      }>(`/api/url-resources/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return response;
    },

    delete: async (id: string) => {
      const response = await this.request<{
        success: boolean;
        message: string;
      }>(`/api/url-resources/${id}`, {
        method: 'DELETE',
      });
      return response;
    },

    search: async (data: {
      query: string;
      client_id?: string;
      url_type?: string;
      limit?: number;
      min_relevance?: number;
    }) => {
      const response = await this.request<{
        success: boolean;
        results: Array<{
          resource: any;
          relevance_score: number;
          matched_keywords: string[];
          generated_response: string;
        }>;
        total: number;
      }>('/api/url-resources/search', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return response;
    },

    getUsage: async (id: string, limit = 20) => {
      const response = await this.request<{
        success: boolean;
        resource_id: string;
        usage: Array<{
          query: string;
          matched_keywords: string[];
          relevance_score: number;
          response_generated: string;
          created_at: string;
        }>;
        total: number;
      }>(`/api/url-resources/${id}/usage?limit=${limit}`);
      return response;
    },

    toggle: async (id: string) => {
      const response = await this.request<{
        success: boolean;
        message: string;
      }>(`/api/url-resources/${id}/toggle`, {
        method: 'POST',
      });
      return response;
    },
  };

  // ========== GRAPH ==========

  graph = {
    getRelationships: async (params?: { client_id?: string; include_types?: string }) => {
      const query = new URLSearchParams();
      if (params?.client_id) query.append('client_id', params.client_id);
      if (params?.include_types) query.append('include_types', params.include_types);

      const queryString = query.toString();
      const url = `/api/graph/relationships${queryString ? `?${queryString}` : ''}`;

      const response = await this.request<{
        success: boolean;
        nodes: Array<{
          id: string;
          type: 'client' | 'server' | 'tool' | 'prompt' | 'resource';
          label: string;
          metadata: any;
        }>;
        edges: Array<{
          id: string;
          source: string;
          target: string;
          type: string;
          label: string;
        }>;
        stats: {
          total_nodes: number;
          total_edges: number;
          nodes_by_type: {
            client: number;
            server: number;
            tool: number;
            prompt: number;
            resource: number;
          };
        };
      }>(url);
      return response;
    },
  };

  // ========== HEALTH CHECK ==========

  health = async () => {
    return this.request<{ status: string; environment: string; timestamp: string }>('/health');
  };
}

export const api = new APIClient();
export default api;
